import type { IntlShape } from 'react-intl'

import { unique } from 'lib/array/arrayUtils'
import { getTripListRowTripItemId } from './tripListCartPrices'
import { isBookableTripListRow } from './tripListRows'
import type { TripListRow } from './tripListTypes'

export function getSelectedTripListRows(
  rows: Array<TripListRow>,
  selectedRowIds: Set<string>,
): Array<TripListRow> {
  return rows.filter(row => selectedRowIds.has(row.id))
}

export function getTripListBookableSelectedRows(rows: Array<TripListRow>): Array<TripListRow> {
  return rows.filter(isBookableTripListRow)
}

export type TripListSelectedBookAction =
  | { kind: 'row', row: TripListRow }
  | { kind: 'cart' }
  | { kind: 'none' }

export function getTripListSelectedBookAction(selectedRows: Array<TripListRow>): TripListSelectedBookAction {
  const bookableRows = getTripListBookableSelectedRows(selectedRows)
  if (bookableRows.length === 0) return { kind: 'none' }
  if (bookableRows.length > 1) return { kind: 'cart' }

  const singleBookableRow = bookableRows[0]
  if (!singleBookableRow) return { kind: 'none' }

  return { kind: 'row', row: singleBookableRow }
}

export function getTripListRowAskAiContext(row: TripListRow): string {
  return `Ask AI about item_id=${getTripListRowTripItemId(row)} (${JSON.stringify(row.title)})`
}

export function getTripListRowMoveAskAiContext(row: TripListRow): string {
  return `The user wants AI help moving item_id=${getTripListRowTripItemId(row)} (${JSON.stringify(row.title)}) to another day or location in the itinerary.`
}

export function getTripListRowsMoveAskAiContext(rows: Array<TripListRow>): string {
  if (rows.length === 1 && rows[0]) return getTripListRowMoveAskAiContext(rows[0])

  const rowContexts = rows.map(row => `item_id=${getTripListRowTripItemId(row)} (${JSON.stringify(row.title)})`)
  return `The user wants AI help moving these selected itinerary items to another day or location in the itinerary:\n${rowContexts.join('\n')}`
}

export function getTripListRowsAskAiContext(rows: Array<TripListRow>): string {
  if (rows.length === 1 && rows[0]) return getTripListRowAskAiContext(rows[0])

  const rowContexts = rows.map(row => `item_id=${getTripListRowTripItemId(row)} (${JSON.stringify(row.title)})`)
  return `Ask AI about these selected itinerary items:\n${rowContexts.join('\n')}`
}

export function getTripListRowsAskAiTitle(intl: IntlShape, rows: Array<TripListRow>): string {
  if (rows.length === 1 && rows[0]) return rows[0].title

  return intl.formatMessage(
    {
      id: 'lyikWpZX-gQjJN6DET4b',
      defaultMessage: '{count, plural, one {# item selected} other {# items selected}}',
      description: 'Visible Ask AI context title for multiple selected trip planner list rows',
    },
    { count: rows.length },
  )
}

export function getTripListRowsMoveAskAiTitle(intl: IntlShape, rows: Array<TripListRow>): string {
  if (rows.length === 1 && rows[0]) {
    return intl.formatMessage(
      {
        id: 'G73Hdsutq3FB8H9OeonT',
        defaultMessage: 'Move {title}',
        description: 'Visible chat input context title when using AI to move a trip planner list row',
      },
      { title: rows[0].title },
    )
  }

  return intl.formatMessage(
    {
      id: 'HalreAt6dyZZclOglhti',
      defaultMessage: 'Move {count, plural, one {# item} other {# items}}',
      description: 'Visible chat input context title when using AI to move multiple selected trip planner list rows',
    },
    { count: rows.length },
  )
}

export function getTripListRowsMoveAskAiPlaceholder(intl: IntlShape, rows: Array<TripListRow>): string {
  if (rows.length === 1 && rows[0]) {
    return intl.formatMessage(
      {
        id: 'FSfob8tN4jy14UaUL0hv',
        defaultMessage: 'Tell me where to move {title}',
        description: 'Chat input placeholder when using AI to move a trip planner list row',
      },
      { title: rows[0].title },
    )
  }

  return intl.formatMessage({
    id: 'NxDztcfT-0Ya1utqMTTG',
    defaultMessage: 'Tell me where to move these items',
    description: 'Chat input placeholder when using AI to move multiple selected trip planner list rows',
  })
}

export function getTripListRowsTripItemIds(rows: Array<TripListRow>): Array<number> {
  return unique(rows.map(getTripListRowTripItemId))
}
